import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { motion, AnimatePresence } from "framer-motion";
import { Plus, Pencil, Trash2, X, Eye, EyeOff, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, mediaUrl } from "../../lib/api";
import { RESOURCE_CONFIG, defaultsFor } from "../../lib/adminSchemas";
import { FieldRenderer } from "../../components/admin/Fields";

export default function ResourceManager({ resource }) {
  const cfg = RESOURCE_CONFIG[resource];
  const qc = useQueryClient();
  const [editing, setEditing] = useState(null); // item or {} for new
  const [form, setForm] = useState({});

  const { data: items = [], isLoading } = useQuery({
    queryKey: ["admin", resource],
    queryFn: async () => (await api.get(`/admin/${resource}`)).data,
  });

  const saveMut = useMutation({
    mutationFn: async (payload) => {
      if (payload.id) return (await api.put(`/admin/${resource}/${payload.id}`, payload)).data;
      return (await api.post(`/admin/${resource}`, payload)).data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin", resource] });
      qc.invalidateQueries({ queryKey: ["admin-stats"] });
      toast.success("Guardado");
      setEditing(null);
    },
    onError: () => toast.error("Error al guardar"),
  });

  const delMut = useMutation({
    mutationFn: async (id) => api.delete(`/admin/${resource}/${id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin", resource] });
      qc.invalidateQueries({ queryKey: ["admin-stats"] });
      toast.success("Eliminado");
    },
  });

  const openNew = () => {
    setForm(defaultsFor(resource));
    setEditing({});
  };
  const openEdit = (item) => {
    setForm({ ...defaultsFor(resource), ...item });
    setEditing(item);
  };

  const togglePublish = (item) => {
    saveMut.mutate({ ...item, published: !item.published });
  };

  const submit = (e) => {
    e.preventDefault();
    const titleField = cfg.titleField;
    if (!form[titleField]?.trim()) {
      toast.error("Completa el campo principal");
      return;
    }
    saveMut.mutate(form);
  };

  return (
    <div>
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-2xl font-bold sm:text-3xl">{cfg.label}</h1>
          <p className="mt-1 text-sm text-white/50">{items.length} elemento(s)</p>
        </div>
        <button onClick={openNew} data-testid={`${resource}-new-btn`} className="inline-flex items-center gap-2 rounded-full bg-glg-blue px-5 py-2.5 text-sm font-semibold text-white hover:scale-[1.03]">
          <Plus className="h-4 w-4" /> Nuevo
        </button>
      </div>

      {isLoading ? (
        <div className="mt-10 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-white/40" /></div>
      ) : items.length === 0 ? (
        <div className="mt-10 rounded-2xl border border-dashed border-white/15 py-16 text-center text-white/40">Aún no hay elementos. Crea el primero.</div>
      ) : (
        <div className="mt-8 space-y-3">
          {items.map((item) => {
            const img = item.image || item.logo;
            return (
              <div key={item.id} data-testid={`${resource}-item`} className="flex items-center gap-4 rounded-2xl border border-white/10 bg-glg-surface/60 p-4">
                <div className="flex h-14 w-14 shrink-0 items-center justify-center overflow-hidden rounded-xl bg-glg-black/60">
                  {img ? <img src={mediaUrl(img)} alt="" className="h-full w-full object-cover" /> : <span className="text-lg font-bold text-white/30">{(item[cfg.titleField] || "?")[0]}</span>}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="truncate font-medium text-white">{item[cfg.titleField]}</div>
                  <div className="mt-0.5 flex items-center gap-2 text-xs text-white/40">
                    <span>Orden: {item.order ?? 0}</span>
                    <span className={`rounded-full px-2 py-0.5 ${item.published ? "bg-glg-green/15 text-glg-green" : "bg-white/10 text-white/50"}`}>
                      {item.published ? "Publicado" : "Oculto"}
                    </span>
                  </div>
                </div>
                <button onClick={() => togglePublish(item)} title="Publicar/Ocultar" className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-white/15 text-white/60 hover:text-white">
                  {item.published ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
                </button>
                <button onClick={() => openEdit(item)} data-testid={`${resource}-edit-btn`} className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-white/15 text-white/60 hover:text-white">
                  <Pencil className="h-4 w-4" />
                </button>
                <button onClick={() => { if (window.confirm("¿Eliminar este elemento?")) delMut.mutate(item.id); }} data-testid={`${resource}-delete-btn`} className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-white/15 text-red-300/70 hover:text-red-300">
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            );
          })}
        </div>
      )}

      <AnimatePresence>
        {editing && (
          <motion.div className="fixed inset-0 z-50 flex justify-end" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
            <div className="absolute inset-0 bg-black/60" onClick={() => setEditing(null)} />
            <motion.form
              onSubmit={submit}
              data-testid={`${resource}-form`}
              initial={{ x: "100%" }}
              animate={{ x: 0 }}
              exit={{ x: "100%" }}
              transition={{ type: "tween", duration: 0.3 }}
              className="relative z-10 flex h-full w-full max-w-lg flex-col border-l border-white/10 bg-glg-surface"
            >
              <div className="flex items-center justify-between border-b border-white/10 px-6 py-4">
                <h2 className="font-heading text-lg font-semibold text-white">{editing.id ? "Editar" : "Nuevo"} {cfg.singular}</h2>
                <button type="button" onClick={() => setEditing(null)} className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-white/15 text-white/60 hover:text-white"><X className="h-4 w-4" /></button>
              </div>
              <div className="flex-1 space-y-5 overflow-y-auto p-6">
                {cfg.fields.map((f) => (
                  <div key={f.key} className={f.type === "boolean" ? "flex items-center justify-between" : ""}>
                    <label className="mb-2 block text-xs font-medium uppercase tracking-wider text-white/50">{f.label}</label>
                    <FieldRenderer field={f} value={form[f.key]} onChange={(v) => setForm((p) => ({ ...p, [f.key]: v }))} />
                  </div>
                ))}
              </div>
              <div className="border-t border-white/10 p-4">
                <button type="submit" disabled={saveMut.isPending} data-testid={`${resource}-save-btn`} className="inline-flex w-full items-center justify-center gap-2 rounded-xl bg-glg-blue px-6 py-3.5 text-sm font-semibold text-white hover:scale-[1.01] disabled:opacity-60">
                  {saveMut.isPending && <Loader2 className="h-4 w-4 animate-spin" />} Guardar
                </button>
              </div>
            </motion.form>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
